/**
 * Base de dados em memória do modo mock (VITE_USE_MOCKS=true).
 *
 * É mutável dentro da sessão: criar/editar/excluir refletem nas telas até o
 * reload. Tudo é gerado de forma determinística para a paginação/ordenação/
 * busca e o "abrir detalhe do lead" ficarem consistentes entre navegações.
 */
import type { AdminClient, Banner, Campaign, Lead, PanelUser, Passbook, PointLog, Product } from '@/shared/types';
import type { FabButton } from '@/shared/types/fab-button';

/* ──────────────────────────── util determinístico ──────────────────────────── */

function mulberry32(seed: number) {
  let a = seed;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function pick<T>(rnd: () => number, arr: T[]): T {
  return arr[Math.floor(rnd() * arr.length)];
}

function daysAgoIso(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString();
}

function pad(n: number, len = 2): string {
  return String(n).padStart(len, '0');
}

/** `dd/mm/yyyy` a partir de um Date local. */
function toBrDate(d: Date): string {
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
}

const WEEKDAY_BR = ['Dom', 'Seg', 'Ter', 'Qua', 'Que', 'Sex', 'Sab'] as const;

/* ──────────────────────────── passbooks (lojas) ──────────────────────────── */

// IDs no padrão do dump (ObjectId do MongoDB). Tea Shop = passbook real do cliente.
const TEASHOP = '65b14fa10142472d1519d0b7';
const ECARE = 'pb_ecare';
const BACIO = 'pb_bacio';
/** Operador — arquétipos com integrado !== true (menu Operador visível). */
const OP_SELOS = 'pb_op_selos';
const OP_CASHBACK = 'pb_op_cashback';
const OP_PONTOS = 'pb_op_pontos';
const OP_GIFTCARD = 'pb_op_giftcard';
const OP_REGUA = 'pb_op_regua';
const OP_PRATILEIRA = 'pb_op_pratileira';
const OP_TAXA = 'pb_op_taxa';
/** Operador — selo por SKU (SMOOV / regra-por-produto). */
const OP_PRODUTO = 'pb_op_produto';
/** Operador — moeda EUR (hasPT). */
const OP_EUR = 'pb_op_eur';
/** Operador — addFields customizados (moneyValorCompra, numberCodCupom, select). */
const OP_ADDFIELDS = 'pb_op_addfields';

function withDemoCampaignFlags(pb: Passbook): Passbook {
  return {
    ...pb,
    hasCampaign: true,
    hasCampaignAdvanced: true,
    hasCampaignMetabase: true,
    hasSimpleCampaignWhatsApp: pb.hasSimpleCampaignWhatsApp ?? true,
  };
}

function buildPassbooks(): Record<string, Passbook> {
  const list: Passbook[] = [
    withDemoCampaignFlags({
      _id: TEASHOP,
      organizationName: 'Tea Shop',
      model: 'storeCard',
      fieldUniqueIdLabel: 'CPF',
      shortUrl: 'https://fixe.club/teashop',
      backgroundColor: 'rgb(0,61,50)',
      foregroundColor: 'rgb(255,255,255)',
      labelColor: 'rgb(180,188,52)',
      hasPoints: true,
      hasProducts: true,
      hasMultiStore: true,
      // Indique e Ganhe ligado: menu, aba no lead, FAB "Indique e ganhe" e /mgm/<id> nos mocks.
      hasMemberGetMember: true,
      hasEmailMarketing: true,
      hasSmsMarketing: true,
      maxInputValidation: 10,
      // Integrado=true → Operador escondido (exceto Admin). Mantido para regressão de menu.
      regraCartao: { status: true, tipo: 'regra-de-valor', regra: 50, multiplicador: 1, integrado: true },
      metabase: [
        { dashboard: 101, aba: 'Visão Geral', multi: true, lojaIndividual: 'Tea Shop Morumbi' },
        { dashboard: 102, aba: 'Vendas', multi: true, lojaIndividual: 'Tea Shop Morumbi' },
        { dashboard: 103, aba: 'Fidelização', multi: false },
      ],
    }),
    withDemoCampaignFlags({
      _id: ECARE,
      organizationName: 'eCare',
      fieldUniqueIdLabel: 'CPF',
      shortUrl: 'https://fixe.club/ecare',
      backgroundColor: 'rgb(0, 122, 110)',
      foregroundColor: 'rgb(240, 255, 252)',
      labelColor: 'rgb(170, 230, 220)',
      hasCashback: true,
      hasPoints: true,
      regraCartao: { status: true, tipo: 'cashback', regra: 1, multiplicador: 1, integrado: true },
      metabase: [
        { dashboard: 201, aba: 'Resumo' },
        { dashboard: 202, aba: 'Cashback' },
      ],
    }),
    withDemoCampaignFlags({
      _id: BACIO,
      organizationName: 'Bacio di Latte',
      fieldUniqueIdLabel: 'CPF',
      shortUrl: 'https://fixe.club/bacio',
      backgroundColor: 'rgb(33, 37, 54)',
      foregroundColor: 'rgb(245, 246, 250)',
      labelColor: 'rgb(180, 186, 210)',
      hasTransaction: true,
      hasProducts: true,
      metabase: [{ dashboard: 301, aba: 'Painel' }],
    }),
    // —— Operador (integrado false) ——
    withDemoCampaignFlags({
      _id: OP_SELOS,
      organizationName: 'Demo Selos (Operador)',
      hasMemberGetMember: true,
      fieldUniqueIdLabel: 'CPF',
      shortUrl: 'https://fixe.club/op-selos',
      backgroundColor: 'rgb(45, 55, 72)',
      foregroundColor: 'rgb(255,255,255)',
      labelColor: 'rgb(255, 200, 100)',
      maxInputValidation: 10,
      /** Espelha Fixe CRM - Selos: wallet lista só discount. */
      redeemType: 'discount',
      hasProducts: true,
      regraCartao: {
        status: true,
        tipo: 'regra-de-valor',
        regra: 50,
        multiplicador: 1,
        integrado: false,
        data_inicio: '2026-01-01',
        data_fim: '2026-12-31',
      },
    }),
    withDemoCampaignFlags({
      _id: OP_CASHBACK,
      organizationName: 'Demo Cashback (Operador)',
      fieldUniqueIdLabel: 'CPF',
      shortUrl: 'https://fixe.club/op-cashback',
      backgroundColor: 'rgb(16, 85, 72)',
      foregroundColor: 'rgb(240, 255, 252)',
      labelColor: 'rgb(120, 220, 190)',
      hasCashback: true,
      hasPoints: true,
      regraCartao: {
        status: true,
        tipo: 'cashback',
        regra: 10,
        integrado: false,
        validaValor: true,
        valor: 20,
        validaValorResgate: true,
        valorResgate: 5,
        cashLimit: 100,
        hasWeek: true,
        week: 0,
        multiplicador: 2,
      },
    }),
    withDemoCampaignFlags({
      _id: OP_PONTOS,
      organizationName: 'Demo Pontos (Operador)',
      fieldUniqueIdLabel: 'CPF',
      shortUrl: 'https://fixe.club/op-pontos',
      backgroundColor: 'rgb(30, 58, 95)',
      foregroundColor: 'rgb(255,255,255)',
      labelColor: 'rgb(147, 197, 253)',
      hasPoints: true,
      hasProducts: true,
      regraCartao: {
        status: true,
        tipo: 'pontos',
        regra: 10,
        integrado: false,
        arredondar: true,
        validaValorResgate: true,
        valorResgate: 50,
        resgateNaoPontua: true,
        cashLimit: 50,
      },
    }),
    withDemoCampaignFlags({
      _id: OP_GIFTCARD,
      organizationName: 'Demo Giftcard (Operador)',
      fieldUniqueIdLabel: 'CPF',
      shortUrl: 'https://fixe.club/op-gift',
      backgroundColor: 'rgb(88, 28, 68)',
      foregroundColor: 'rgb(255,245,250)',
      labelColor: 'rgb(251, 182, 206)',
      hasGiftcard: true,
      hasTransaction: true,
      regraCartao: { status: true, integrado: false },
    }),
    withDemoCampaignFlags({
      _id: OP_REGUA,
      organizationName: 'Demo Régua Cashback (Operador)',
      fieldUniqueIdLabel: 'CPF',
      shortUrl: 'https://fixe.club/op-regua',
      backgroundColor: 'rgb(55, 48, 20)',
      foregroundColor: 'rgb(255,250,240)',
      labelColor: 'rgb(250, 204, 120)',
      hasCashback: true,
      hasPoints: true,
      regraCartao: {
        status: true,
        tipo: 'cashback',
        regra: 5,
        integrado: false,
        regua: true,
        regraRegua: [
          { valorMin: 0, valorMax: 99.99, quantidade: 5 },
          { valorMin: 100, valorMax: 99999, quantidade: 12 },
        ],
        resgateNaoPontua: false,
      },
    }),
    withDemoCampaignFlags({
      _id: OP_PRATILEIRA,
      organizationName: 'Demo Pratileira (Operador)',
      fieldUniqueIdLabel: 'CPF',
      shortUrl: 'https://fixe.club/op-prat',
      backgroundColor: 'rgb(40, 30, 60)',
      foregroundColor: 'rgb(250,245,255)',
      labelColor: 'rgb(196, 181, 253)',
      hasPoints: true,
      hasProducts: true,
      regraCartao: {
        status: true,
        tipo: 'pontos',
        regra: 1,
        integrado: false,
        pratileira: true,
        oneOrderPerDay: true,
      },
    }),
    withDemoCampaignFlags({
      _id: OP_TAXA,
      organizationName: 'Demo Selos Taxa (Operador)',
      fieldUniqueIdLabel: 'CPF',
      shortUrl: 'https://fixe.club/op-taxa',
      backgroundColor: 'rgb(20, 50, 40)',
      foregroundColor: 'rgb(240,255,250)',
      labelColor: 'rgb(110, 231, 183)',
      maxInputValidation: 8,
      redeemType: 'discount',
      hasProducts: true,
      regraCartao: {
        status: true,
        tipo: 'regra-fixa',
        integrado: false,
        validaValor: true,
        valor: 80,
        quantidade: 2,
      },
    }),
    withDemoCampaignFlags({
      _id: OP_PRODUTO,
      organizationName: 'Demo Selos por Produto (Operador)',
      fieldUniqueIdLabel: 'CPF',
      shortUrl: 'https://fixe.club/op-produto',
      backgroundColor: 'rgb(60, 40, 20)',
      foregroundColor: 'rgb(255,250,240)',
      labelColor: 'rgb(251, 191, 36)',
      maxInputValidation: 10,
      hasProducts: true,
      regraCartao: {
        status: true,
        tipo: 'regra-por-produto',
        integrado: false,
      },
    }),
    withDemoCampaignFlags({
      _id: OP_EUR,
      organizationName: 'Demo EUR (Operador)',
      fieldUniqueIdLabel: 'NIF',
      shortUrl: 'https://fixe.club/op-eur',
      backgroundColor: 'rgb(30, 64, 120)',
      foregroundColor: 'rgb(255,255,255)',
      labelColor: 'rgb(147, 197, 253)',
      hasPoints: true,
      hasCashback: true,
      hasPT: true,
      regraCartao: {
        status: true,
        tipo: 'cashback',
        regra: 10,
        integrado: false,
        validaValor: true,
        valor: 15,
      },
    }),
    withDemoCampaignFlags({
      _id: OP_ADDFIELDS,
      organizationName: 'Demo Campos Extras (Operador)',
      fieldUniqueIdLabel: 'CPF',
      shortUrl: 'https://fixe.club/op-campos',
      backgroundColor: 'rgb(55, 65, 81)',
      foregroundColor: 'rgb(255,255,255)',
      labelColor: 'rgb(209, 213, 219)',
      hasPoints: true,
      hasCashback: true,
      showAddFields: true,
      addFields: [
        {
          label: 'Valor da compra',
          slug: 'moneyValorCompra',
          type: 'money',
          display: true,
          required: false,
        },
        {
          label: 'Código do Cupom',
          slug: 'numberCodCupom',
          type: 'number',
          display: true,
          required: true,
        },
        {
          label: 'Canal de venda',
          slug: 'canalVenda',
          type: 'select',
          display: true,
          required: true,
          options: 'Balcão|Delivery|Drive-thru',
        },
        {
          label: 'Observações',
          slug: 'obsPedido',
          type: 'textarea',
          display: true,
          required: false,
        },
      ],
      regraCartao: {
        status: true,
        tipo: 'cashback',
        regra: 10,
        integrado: false,
      },
    }),
    // —— Área do participante (/clube/pb_demo_loccitane) — cores e regras do passbook real ——
    withDemoCampaignFlags({
      _id: 'pb_demo_loccitane',
      organizationName: "L'Occitane au Brésil (Demo)",
      description: 'Cartão Fidelidade',
      slug: 'loccitaneaubresil',
      model: 'storeCard',
      fieldUniqueIdLabel: 'CPF',
      backgroundColor: 'rgb(166,25,46)',
      foregroundColor: 'rgb(245,225,204)',
      labelColor: 'rgb(245,225,204)',
      hasPoints: true,
      hasTiers: true,
      tiers: [
        {
          level: 'invite',
          label: 'Invité',
          threshold: 0,
          order: 1,
          color: '#c4a574',
          backgroundColor: 'rgb(196,165,116)',
          foregroundColor: 'rgb(255,248,235)',
          labelColor: 'rgb(255,248,235)',
          contentBenefits: '1 ponto por real em compras e acesso às promoções do clube.',
        },
        {
          level: 'privilege',
          label: 'Privilège',
          threshold: 500,
          order: 2,
          color: '#a6192e',
          backgroundColor: 'rgb(166,25,46)',
          foregroundColor: 'rgb(245,225,204)',
          labelColor: 'rgb(245,225,204)',
          contentBenefits: 'Benefícios do Invité + convites para lançamentos e uma amostra em compras selecionadas.',
        },
        {
          level: 'prestige',
          label: 'Prestige',
          threshold: 1500,
          order: 3,
          color: '#5c1a2e',
          backgroundColor: 'rgb(92,26,46)',
          foregroundColor: 'rgb(245,225,204)',
          labelColor: 'rgb(245,225,204)',
          contentBenefits: 'Benefícios do Privilège + wrapping exclusivo e prioridade em pré-vendas.',
        },
        {
          level: 'ambassadeur',
          label: 'Ambassadeur',
          threshold: 4000,
          order: 4,
          color: '#1c1917',
          backgroundColor: 'rgb(28,25,23)',
          foregroundColor: 'rgb(232,213,163)',
          labelColor: 'rgb(232,213,163)',
          contentBenefits: 'Nível máximo: experiências exclusivas e presente especial no aniversário.',
        },
      ],
      hasMemberGetMember: true,
      hasCoupons: true,
      couponName: 'Frete grátis',
      couponStoreUrl: 'https://br.loccitaneaubresil.com/',
      regraCartao: { status: true, regra: 1, tipo: 'pontos', pratileira: true, hasSurvey: true, idSurvey: 'srv_demo_public' },
    }),
    // —— Área do participante com campos extras no cadastro (Meus dados se adapta) ——
    withDemoCampaignFlags({
      _id: 'pb_demo_clube_campos',
      organizationName: 'Empório Brownie (Demo)',
      description: 'Clube Empório Brownie',
      slug: 'emporiobrownie',
      fieldUniqueIdLabel: 'CPF',
      backgroundColor: 'rgb(78, 52, 46)',
      foregroundColor: 'rgb(255, 236, 179)',
      hasPoints: true,
      addFields: [
        { label: 'Loja Favorita *', slug: 'lojaFavorita', type: 'select', size: 6, required: true, options: 'Shopping Iguatemi|Vila Madalena|Moema' },
        { label: 'Como conheceu', slug: 'comoConheceu', type: 'radio', size: 6, options: 'Indicação|Instagram|Outro', optionalField: 'Conte pra gente' },
        { label: 'Interesses', slug: 'interesses', type: 'checkbox', size: 12, options: 'Brownies|Bolos|Cafés' },
      ],
    }),
    // —— Cadastro público (public-invite) — uma loja demo por variação da Fase 2 ——
    withDemoCampaignFlags({
      _id: 'pb_demo_cnpj',
      organizationName: 'Cia da Macaxeira B2B (Demo)',
      slug: 'ciadamacaxeiraclub',
      backgroundColor: 'rgb(101, 67, 33)',
      foregroundColor: 'rgb(255,255,255)',
      hasPoints: true,
    }),
    withDemoCampaignFlags({
      _id: 'pb_demo_placa',
      organizationName: 'GoFlashWash (Demo)',
      slug: 'goflashwash',
      backgroundColor: 'rgb(2, 132, 199)',
      foregroundColor: 'rgb(255,255,255)',
      hasPoints: true,
      addFields: [
        { label: 'CPF do responsável', slug: 'cpfResponsavel', type: 'text', size: 12, required: true },
      ],
    }),
    withDemoCampaignFlags({
      _id: 'pb_demo_intl',
      organizationName: 'Ardevilla (Demo PT)',
      slug: 'ardevilla',
      hasPT: true,
      backgroundColor: 'rgb(21, 128, 61)',
      foregroundColor: 'rgb(255,255,255)',
      hasPoints: true,
    }),
    withDemoCampaignFlags({
      _id: 'pb_demo_estado',
      organizationName: 'Vip Cell (Demo)',
      slug: 'vipcell',
      fieldUniqueIdLabel: 'CPF',
      backgroundColor: 'rgb(126, 34, 206)',
      foregroundColor: 'rgb(255,255,255)',
      hasPoints: true,
    }),
    withDemoCampaignFlags({
      _id: 'pb_demo_abf',
      organizationName: 'ABF (Demo)',
      slug: 'abf',
      backgroundColor: 'rgb(180, 83, 9)',
      foregroundColor: 'rgb(255,255,255)',
      hasPoints: true,
      addFields: [
        {
          label: 'Loja Favorita',
          slug: 'lojaFavorita',
          type: 'select',
          size: 12,
          required: true,
          options: 'Shopping Center|Rua Augusta|Online',
        },
      ],
    }),
    // —— Fase 3: grupos de dependentes + app white-label ——
    withDemoCampaignFlags({
      _id: 'pb_demo_circus',
      organizationName: 'Circus (Demo)',
      slug: 'circus',
      backgroundColor: 'rgb(190, 24, 93)',
      foregroundColor: 'rgb(255,255,255)',
      hasPoints: true,
    }),
    withDemoCampaignFlags({
      _id: 'pb_demo_brincarte',
      organizationName: 'Brincarte (Demo)',
      slug: 'brincarte',
      backgroundColor: 'rgb(202, 138, 4)',
      foregroundColor: 'rgb(255,255,255)',
      hasPoints: true,
      addFields: [
        { label: 'WhatsApp do Responsável', slug: 'whatsappResponsavel', type: 'tel', size: 12, required: true },
      ],
    }),
    withDemoCampaignFlags({
      _id: 'pb_demo_flavor',
      organizationName: 'Mr. Cheney (Demo App Próprio)',
      hasFlavor: 'com.fixecrm.mrcheney.wallet.app',
      backgroundColor: 'rgb(120, 53, 15)',
      foregroundColor: 'rgb(255,255,255)',
      hasPoints: true,
    }),
    // —— Fase 4: cartão-presente ——
    withDemoCampaignFlags({
      // Nome do template legado ("invite_omatuto.ejs") é só histórico — a loja real "O Matuto"
      // (slug=omatuto) NÃO tem hasGiftcard hoje (ela é, na verdade, um caso hasFlavor/Fase 3:
      // com.fixecrm.omatutochurrascaria.wallet.app). Exemplos reais confirmados com hasGiftcard
      // nesse fluxo genérico: Dachô Sushi, Ariga Sushi, Fixe Card, O Matuto Card (sub-lojas).
      _id: 'pb_demo_gift_omatuto',
      organizationName: 'Cartão-presente genérico (Demo)',
      hasGiftcard: true,
      backgroundColor: 'rgb(146, 64, 14)',
      foregroundColor: 'rgb(255,255,255)',
    }),
    withDemoCampaignFlags({
      _id: 'pb_demo_gift_trintaeum',
      organizationName: 'Trinta e Um Café da Manhã (Demo)',
      hasGiftcard: true,
      slug: 'broa',
      backgroundColor: 'rgb(180, 130, 40)',
      foregroundColor: 'rgb(255,255,255)',
    }),
    withDemoCampaignFlags({
      _id: 'pb_demo_gift_trintaeum_livre',
      organizationName: 'Trinta e Um Cozinha Mineira (Demo)',
      hasGiftcard: true,
      slug: 'trintaeumcozinhamineira',
      backgroundColor: 'rgb(120, 80, 20)',
      foregroundColor: 'rgb(255,255,255)',
    }),
  ];
  return Object.fromEntries(list.map((p) => [p._id, p]));
}

/* ──────────────────────────── leads ──────────────────────────── */

const FIRST = ['Ana', 'Bruno', 'Carla', 'Diego', 'Elaine', 'Felipe', 'Gabriela', 'Hélio', 'Isabela', 'João', 'Karina', 'Lucas', 'Marina', 'Nelson', 'Olívia', 'Paulo', 'Quésia', 'Rafael', 'Sofia', 'Tiago', 'Úrsula', 'Vitor', 'Wagner', 'Yara', 'Zélia'];
const LAST = ['Silva', 'Souza', 'Oliveira', 'Santos', 'Pereira', 'Costa', 'Almeida', 'Ferreira', 'Rodrigues', 'Gomes', 'Martins', 'Araújo', 'Barbosa', 'Ribeiro', 'Valente'];

// Catálogo Tea Shop (chás, açaí, espresso, presentes) — SKUs no padrão do dump.
const PRODUCT_NAMES: Array<[string, string]> = [
  ['270181', 'Mediterrâneo 50G'],
  ['270631', 'Nutty Choco 50G'],
  ['280101', 'Golden Cake 50G'],
  ['280051', 'Cocoa Orange 50G'],
  ['280171', 'Wild Berries 50G'],
  ['281221', 'Watermelon Paradise 50G'],
  ['12102.CX', 'Azahar Oolong - Caixa Sachês Piramidais (10 Sachês)'],
  ['75117', 'Chá Gaseificado - Rooibos Tangerina'],
  ['101151', 'Açaí 300ml'],
  ['101152', 'Açaí 500ml'],
  ['097', 'Espresso Pq'],
  ['098', 'Espresso Gd'],
  ['112233', 'Água São Jorge'],
  ['71363', 'Lata Circular Chiara 250G'],
  ['80080', 'Caixa Presente Com Acetato Pequena'],
  ['111511', 'Kit Azeites Tea Shop'],
  ['82663', 'Pack Sachês'],
];
// Produtos de resgate (produto_resgate no dump).
const RESGATE_PRODUCTS = [
  '097 - Top&Tea Caramelo 8Un Fidelidade',
  'CAN001 - Caneca Tea Shop',
  'INF010 - Infusor de Chá Inox',
  'KIT005 - Kit Degustação 5 Chás',
];
// Origem da transação (campo `source`/`storeName` do dump = integração PDV).
const SOURCES = ['NextSoft Soluções', 'Tea Shop - Shopping Iguatemi', 'Tea Shop - Loja Online', 'Tea Shop - Morumbi'];
const MOCK_STORES = [
  { storeCod: 'lj0001', storeName: 'Morumbi' },
  { storeCod: 'lj0002', storeName: 'Moema' },
  { storeCod: 'lj0003', storeName: 'Lapa' },
  { storeCod: 'lj0004', storeName: 'Eldorado' },
  { storeCod: 'lj0005', storeName: 'Osasco' },
];
// typeAccess espelha o dump: 'Android' | 'APP-ios' | 'iOS' | none.
const PLATFORMS = ['APP-ios', 'Android', 'gwallet', 'none'] as const;

function randomCpf(rnd: () => number): string {
  let s = '';
  for (let i = 0; i < 11; i++) s += Math.floor(rnd() * 10);
  return `${s.slice(0, 3)}.${s.slice(3, 6)}.${s.slice(6, 9)}-${s.slice(9)}`;
}

/** ObjectId-like (24 hex), no padrão `serialNumber`/`_id` do dump. */
function objectId(rnd: () => number): string {
  let s = '';
  for (let i = 0; i < 24; i++) s += Math.floor(rnd() * 16).toString(16);
  return s;
}

/** UUID v4-like — padrão de `externalId`/`numberCodCupom` (Código da Compra) do dump. */
function uuid(rnd: () => number): string {
  const hex = (n: number) => Array.from({ length: n }, () => Math.floor(rnd() * 16).toString(16)).join('');
  return `${hex(8)}-${hex(4)}-4${hex(3)}-${'89ab'[Math.floor(rnd() * 4)]}${hex(3)}-${hex(12)}`;
}

/** Telefone BR no formato do dump: "(11) 99200-2713". */
function brPhone(rnd: () => number): string {
  const ddd = pick(rnd, ['11', '21', '31', '41', '51', '61', '71', '85']);
  const p1 = 90000 + Math.floor(rnd() * 9999);
  const p2 = 1000 + Math.floor(rnd() * 8999);
  return `(${ddd}) ${p1}-${p2}`;
}

function buildLeads(passbookId: string, count: number, isCashback: boolean): Lead[] {
  const seedBase = passbookId.split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  const leads: Lead[] = [];

  for (let i = 0; i < count; i++) {
    const rnd = mulberry32(seedBase * 1000 + i);
    const first = pick(rnd, FIRST);
    const name = `${first} ${pick(rnd, LAST)}`;
    const gender = ['o', 'e', 'i', 'u'].includes(first.slice(-1).toLowerCase()) ? 'M' : 'F';
    const platform = pick(rnd, PLATFORMS as unknown as string[]);
    const installed = platform !== 'none';
    const source = pick(rnd, SOURCES);
    const storeCount = 1 + Math.floor(rnd() * 3);
    const leadStores = Array.from({ length: storeCount }, () => pick(rnd, MOCK_STORES).storeCod);
    const uniqueStores = [...new Set(leadStores)];

    // Série de compras (rule: 1 ponto a cada R$ 50). Saldo acumulado = currentBalance.
    const nLogs = 1 + Math.floor(rnd() * 6);
    const logs: PointLog[] = [];
    let balance = 0;

    // Log inicial de cadastro (action 'input', value 0) — todo usuário tem no dump.
    const createdDaysAgo = 30 + Math.floor(rnd() * 400);
    logs.push({
      _id: objectId(rnd),
      action: 'input',
      value: 0,
      date: daysAgoIso(createdDaysAgo),
      loja: source,
    });

    for (let j = 0; j < nLogs; j++) {
      const roll = rnd();
      const action = roll > 0.92 ? 'cancel' : roll > 0.82 ? 'resgate' : 'addPoints';
      const price = action === 'addPoints' ? Math.round((50 + rnd() * 650) * 100) / 100 : 0;
      const earned = action === 'addPoints' ? Math.max(1, Math.floor(price / 50)) : 0;

      let value: number;
      if (action === 'addPoints') {
        value = isCashback ? Math.round(price * 0.05 * 100) / 100 : earned;
        balance += value;
      } else if (action === 'resgate') {
        value = -(2 + Math.floor(rnd() * 8));
        balance = Math.max(0, balance + value);
      } else {
        value = -(1 + Math.floor(rnd() * 5));
        balance = Math.max(0, balance + value);
      }

      const nItems = 1 + Math.floor(rnd() * 6);
      // details no formato exato do dump: { sku, name, count }.
      const products =
        action === 'addPoints'
          ? Array.from({ length: nItems }, () => {
              const [sku, name] = pick(rnd, PRODUCT_NAMES);
              return { sku, name, count: 1 + Math.floor(rnd() * 3) };
            })
          : undefined;

      logs.push({
        _id: objectId(rnd),
        action: action as 'addPoints' | 'resgate' | 'cancel',
        value,
        date: daysAgoIso(Math.floor(rnd() * (createdDaysAgo - 1))),
        loja: source,
        price: action === 'addPoints' ? price : undefined,
        externalId: uuid(rnd),
        produtoResgate: action === 'resgate' ? pick(rnd, RESGATE_PRODUCTS) : undefined,
        products,
      });
    }
    logs.sort((a, b) => b.date.localeCompare(a.date));

    const deviceArr = installed ? [{ device: objectId(rnd) }] : [];
    const observacao = i % 7 === 0 ? 'Cliente VIP — prefere contato por WhatsApp.' : undefined;
    const lastCardResendAt =
      i % 11 === 0 && installed ? daysAgoIso(Math.floor(rnd() * 2)) : undefined;
    leads.push({
      _id: objectId(rnd),
      profile: { name, uniqueId: randomCpf(rnd) },
      email: `${name.toLowerCase().normalize('NFD').replace(/[^a-z]/g, '.')}@teashop.com.br`,
      phone: brPhone(rnd),
      birthday: `${pad(1 + Math.floor(rnd() * 28))}/${pad(1 + Math.floor(rnd() * 12))}/${1970 + Math.floor(rnd() * 35)}`,
      gender,
      total_points: Math.round(balance * 100) / 100,
      serialNumber: installed ? objectId(rnd) : undefined,
      hashRegenerate: installed ? objectId(rnd) : undefined,
      storeName: source,
      stores: uniqueStores,
      observacao,
      lastCardResendAt,
      createdAt: daysAgoIso(createdDaysAgo),
      updated: logs[0]?.date ?? daysAgoIso(Math.floor(rnd() * 100)),
      typeAccess: platform === 'gwallet' ? 'Android' : platform === 'none' ? undefined : platform,
      deviceLibraryIdentifier: platform === 'APP-ios' || platform === 'iOS' ? deviceArr : [],
      deviceToken:
        platform === 'APP-ios'
          ? [{ typeAccess: 'APP-ios' }]
          : platform === 'APP-android' || platform === 'Android'
            ? [{ typeAccess: 'APP-android' }]
            : [],
      deviceGWallet: platform === 'gwallet' ? deviceArr : [],
      logs,
      id_passbooks: passbookId,
    });
  }
  return leads;
}

/* ──────────────────────────── produtos ──────────────────────────── */

/** Catálogo completo estilo Tea Shop — value String, discount, enabled:false. */
function buildTeaShopProducts(passbookId: string): Product[] {
  return [
    {
      _id: `${passbookId}-prod-00`,
      uniqueId: '097',
      name: 'Top&Tea Caramelo 8Un Fidelidade',
      value: '10',
      price: 0,
      redemption: true,
      redemptionType: 'product',
      enabled: true,
      idPassbook: passbookId,
    },
    {
      _id: `${passbookId}-prod-01`,
      uniqueId: 'CAN001',
      name: 'Caneca Tea Shop',
      value: '25',
      price: 0,
      redemption: true,
      redemptionType: 'product',
      enabled: true,
      idPassbook: passbookId,
    },
    {
      _id: `${passbookId}-prod-02`,
      uniqueId: 'DESC05',
      name: 'Desconto R$ 5 no caixa',
      value: '8',
      price: 5,
      redemption: true,
      redemptionType: 'discount',
      enabled: true,
      idPassbook: passbookId,
    },
    {
      _id: `${passbookId}-prod-03`,
      uniqueId: 'DESC10',
      name: 'Desconto R$ 10 no caixa',
      value: '15',
      price: 10,
      redemption: true,
      redemptionType: 'discount',
      enabled: true,
      idPassbook: passbookId,
    },
    {
      _id: `${passbookId}-prod-04`,
      uniqueId: 'INF010',
      name: 'Infusor de Chá Inox (indisponível)',
      value: '15',
      price: 0,
      redemption: true,
      redemptionType: 'product',
      enabled: false,
      idPassbook: passbookId,
    },
    {
      _id: `${passbookId}-prod-05`,
      uniqueId: 'KIT005',
      name: 'Kit Degustação 5 Chás',
      value: '40',
      price: 0,
      redemption: true,
      redemptionType: 'product',
      enabled: true,
      idPassbook: passbookId,
    },
    {
      _id: `${passbookId}-prod-06`,
      uniqueId: '281221',
      name: 'Watermelon Paradise 50G',
      value: '0',
      price: 0,
      redemption: false,
      redemptionType: 'product',
      idPassbook: passbookId,
    },
    {
      _id: `${passbookId}-prod-07`,
      uniqueId: '71363',
      name: 'Lata Circular Chiara 250G',
      value: '0',
      price: 0,
      redemption: false,
      redemptionType: 'product',
      idPassbook: passbookId,
    },
  ];
}

/**
 * Produtos resgatáveis (cashback / pratileira / giftcard).
 * `value` = custo no saldo do programa; `price` = desconto em R$ se discount.
 * Em cashback/giftcard o saldo já é monetário → value ≈ price nos vouchers.
 */
function buildRedemptionCatalog(passbookId: string): Product[] {
  const cashLike =
    passbookId === OP_CASHBACK ||
    passbookId === OP_GIFTCARD ||
    passbookId === OP_ADDFIELDS ||
    passbookId === OP_EUR ||
    passbookId === OP_REGUA;

  return [
    {
      _id: `${passbookId}-red-00`,
      uniqueId: 'VOUCHER5',
      name: 'Voucher R$ 5',
      value: cashLike ? '5' : '50',
      price: 5,
      redemption: true,
      redemptionType: 'discount',
      enabled: true,
      idPassbook: passbookId,
    },
    {
      _id: `${passbookId}-red-01`,
      uniqueId: 'VOUCHER10',
      name: 'Voucher R$ 10',
      value: cashLike ? '10' : '100',
      price: 10,
      redemption: true,
      redemptionType: 'discount',
      enabled: true,
      idPassbook: passbookId,
    },
    {
      _id: `${passbookId}-red-02`,
      uniqueId: 'BRINDE01',
      name: 'Brinde Surpresa',
      value: cashLike ? '15' : '30',
      price: 0,
      redemption: true,
      redemptionType: 'product',
      enabled: true,
      idPassbook: passbookId,
    },
    {
      _id: `${passbookId}-red-03`,
      uniqueId: 'BRINDE99',
      name: 'Brinde Esgotado',
      value: cashLike ? '8' : '20',
      price: 0,
      redemption: true,
      redemptionType: 'product',
      enabled: false,
      idPassbook: passbookId,
    },
  ];
}

/**
 * Selos + redeemType discount (Fixe CRM - Selos).
 * `value` = custo em selos; `price` = R$ abatidos.
 * `redemption: false` de propósito — wallet lista só por redemptionType.
 */
function buildStampsDiscountCatalog(passbookId: string): Product[] {
  return [
    {
      _id: `${passbookId}-selo-vale20`,
      uniqueId: 'VALE20',
      name: 'Vale 20',
      value: '10',
      price: 20,
      redemption: false,
      redemptionType: 'discount',
      enabled: true,
      idPassbook: passbookId,
    },
    {
      _id: `${passbookId}-selo-vale50`,
      uniqueId: 'VALE50',
      name: 'Vale 50',
      value: '20',
      price: 50,
      redemption: false,
      redemptionType: 'discount',
      enabled: true,
      idPassbook: passbookId,
    },
    {
      _id: `${passbookId}-selo-vale10`,
      uniqueId: 'VALE10',
      name: 'Vale 10',
      value: '5',
      price: 10,
      redemption: false,
      redemptionType: 'discount',
      enabled: true,
      idPassbook: passbookId,
    },
    {
      _id: `${passbookId}-selo-cortesia`,
      uniqueId: 'CORT01',
      name: 'Cortesia (não deve listar)',
      value: '1',
      price: 0,
      redemption: true,
      redemptionType: 'courtesy',
      enabled: true,
      idPassbook: passbookId,
    },
    {
      _id: `${passbookId}-selo-sku`,
      uniqueId: 'SKU-AWARD',
      name: 'SKU que pontua (não deve listar no resgate)',
      value: '0',
      price: 0,
      redemption: false,
      redemptionType: 'product',
      enabled: true,
      idPassbook: passbookId,
    },
  ];
}

/** Catálogo estilo SMOOV — SKUs usados no picker do Operador (regra-por-produto, redemption:false). */
function buildProductRuleCatalog(passbookId: string): Product[] {
  return [
    ['Combo Família', '110148', 0, false],
    ['Burger Clássico', '110147', 0, false],
    ['Batata Grande', '110279', 0, false],
    ['Milkshake Chocolate', '110301', 0, false],
    ['Refrigerante 500ml', '110220', 0, false],
    ['Sobremesa do Dia', '110410', 0, false],
    ['Kids Menu', '110155', 0, false],
    ['Onion Rings', '110288', 0, false],
  ].map(([name, uniqueId, value, redemption], i) => ({
    _id: `${passbookId}-sku-${pad(i)}`,
    uniqueId: uniqueId as string,
    name: name as string,
    value: value as number,
    price: value as number,
    redemption: redemption as boolean,
    redemptionType: 'product' as const,
    idPassbook: passbookId,
  }));
}

/* ──────────────────────────── banners ──────────────────────────── */

const BANNER_IMG = (seed: string) => `https://picsum.photos/seed/${seed}/850/400`;

function buildBanners(passbookId: string): Banner[] {
  return [
    ['Watermelon Paradise', 'Novo chá branco com aroma de melancia. Refrescante e antioxidante 🍉', true],
    ['Programa de Fidelidade', 'Ganhe 1 ponto a cada R$ 50 e troque por prêmios.', true],
    ['Linha de Inverno', 'Blends quentinhos para os dias frios.', false],
    ['Tea Lovers', 'Resgate produtos exclusivos com seus pontos.', true],
  ].map(([title, description, active], i) => ({
    _id: `${passbookId}-banner-${pad(i)}`,
    banner: BANNER_IMG(`${passbookId}-${i}`),
    title: title as string,
    description: description as string,
    active: active as boolean,
    order: i,
    url: 'https://fixe.club',
    entryDate: i % 2 === 0 ? daysAgoIso(20) : null,
    exitDate: i % 2 === 0 ? daysAgoIso(-30) : null,
    expiry: i % 2 === 0,
  }));
}

/* ──────────────────────────── botões flutuantes (FAB) ──────────────────────────── */

function buildFabButtons(passbookId: string): FabButton[] {
  const seed: Array<[string, FabButton['type'], string, string, boolean]> = [
    ['Fale conosco', 'whatsapp', '(11) 97023-3978', '#25D366', true],
    ['Instagram', 'instagram', '@teashopbrasil', '#E1306C', true],
    ['Nosso site', 'link', 'https://teashop.com.br', '#2563eb', true],
    ['Encontre uma loja', 'location', 'https://maps.google.com/?q=tea+shop', '#0EA5E9', false],
  ];
  return seed.map(([label, type, value, color, active], i) => ({
    _id: `${passbookId}-fab-${pad(i)}`,
    label,
    type,
    value,
    color,
    active,
    order: i,
  }));
}

/* ──────────────────────────── campanhas ──────────────────────────── */

function buildCampaigns(passbookId: string, mode: 'email' | 'whatsapp'): Campaign[] {
  const titles = {
    email: ['Boas-vindas Tea Lovers', 'Lançamento Watermelon Paradise', 'Aniversariantes do mês', 'Reativação de inativos'],
    whatsapp: ['Lembrete de pontos a expirar', 'Oferta relâmpago de chás', 'Novidade da semana'],
  }[mode];

  return titles.map((name, i) => {
    const received = 200 + i * 137;
    const logs =
      mode === 'whatsapp'
        ? Array.from({ length: received }, (_, k) => ({
            _id: `${passbookId}-wlog-${i}-${k}`,
            id_passbook: passbookId,
            name: `Contato ${k + 1}`,
            phone: `619${pad(k, 8)}`,
            error: k % 11 === 0,
            created_at: daysAgoIso(i * 3 + (k % 5)),
          }))
        : undefined;
    return {
      _id: `${passbookId}-camp-${mode}-${pad(i)}`,
      name,
      description: `Campanha "${name}" enviada para a base.`,
      message: `Olá! Esta é a campanha ${name}. Aproveite as novidades da loja!`,
      updated: daysAgoIso(i * 4 + 1),
      received,
      status: i === 0 ? 'sent' : i === titles.length - 1 ? 'scheduled' : 'sent',
      logs,
    };
  });
}

/**
 * Campanhas automatizadas com datas relativas ao “agora”.
 * Garante itens no filtro padrão da Agenda (Hoje) e variedade de canal/frequência.
 * Exportada para o handler regenerar a cada GET (evita ficar stale no filtro "Hoje").
 */
export function buildAutomationCampaigns(passbookId: string, seed = 0): Campaign[] {
  const now = new Date();
  const laterHour = Math.min(23, Math.max(now.getHours() + 1, 1));
  const laterHoras = `${pad(laterHour)}:30`;
  const earlyHoras = '08:00';

  const todayBr = toBrDate(now);
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowBr = toBrDate(tomorrow);
  const inThree = new Date(now);
  inThree.setDate(inThree.getDate() + 3);
  const inThreeBr = toBrDate(inThree);

  const todayWeekday = WEEKDAY_BR[now.getDay()]!;
  const channelCycle = ['push', 'email', 'sms', 'whatsapp'] as const;
  const channel = channelCycle[seed % channelCycle.length]!;

  const rows: Array<Partial<Campaign> & { name: string }> = [
    {
      name: 'Disparo diário (ainda hoje)',
      active: true,
      tipo_disparo: channel,
      frequencia: '1',
      frequencia_horas: laterHoras,
      expirar: false,
    },
    {
      name: 'Semanal — dia de hoje',
      active: true,
      tipo_disparo: 'email',
      frequencia: '7',
      frequencia_dia_semana: todayWeekday,
      frequencia_horas: laterHoras,
      expirar: false,
    },
    {
      name: 'Mensal — dia do mês',
      active: true,
      tipo_disparo: 'sms',
      frequencia: '30',
      frequencia_dia: now.getDate(),
      frequencia_horas: '21:00',
      expirar: false,
    },
    {
      name: 'Única — hoje',
      active: true,
      tipo_disparo: 'whatsapp',
      frequencia: '31',
      frequencia_unico: todayBr,
      frequencia_horas: laterHoras,
      expirar: false,
    },
    {
      name: 'Única — amanhã',
      active: true,
      tipo_disparo: 'push',
      frequencia: '31',
      frequencia_unico: tomorrowBr,
      frequencia_horas: '09:15',
      expirar: false,
    },
    {
      name: 'Única — em 3 dias',
      active: true,
      tipo_disparo: 'email',
      frequencia: '31',
      frequencia_unico: inThreeBr,
      frequencia_horas: '11:00',
      expirar: false,
    },
    {
      name: 'Diária já disparada (amanhã)',
      active: true,
      tipo_disparo: 'push',
      frequencia: '1',
      frequencia_horas: earlyHoras,
      expirar: false,
    },
    {
      name: 'Inativa — não aparece por padrão',
      active: false,
      tipo_disparo: 'push',
      frequencia: '1',
      frequencia_horas: laterHoras,
      expirar: false,
    },
    {
      name: 'Campanha Automatizada de Aniversário',
      active: false,
      tipo_campanha: 'A',
      tipo_disparo: 'push',
      frequencia: '1',
      frequencia_horas: '11:46',
      expirar: false,
    },
  ];

  return rows.map((row, i) => ({
    _id: `${passbookId}-camp-automation-${pad(i)}`,
    name: row.name,
    description: `Automação mock "${row.name}".`,
    message: `Olá! Campanha automatizada ${row.name}.`,
    updated: daysAgoIso(i + 1),
    created_at: daysAgoIso(i + 2),
    received: 80 + i * 40 + seed * 3,
    status: 'scheduled',
    active: row.active,
    tipo_disparo: row.tipo_disparo,
    frequencia: row.frequencia,
    frequencia_horas: row.frequencia_horas,
    frequencia_unico: row.frequencia_unico,
    frequencia_dia: row.frequencia_dia,
    frequencia_dia_semana: row.frequencia_dia_semana,
    expirar: row.expirar ?? false,
    atividade: i === 2 ? '30' : '',
    tipo_campanha: row.tipo_campanha ?? 'C',
  }));
}

/* ──────────────────────────── clientes (admin) + usuários ──────────────────────────── */

interface MockDb {
  passbooks: Record<string, Passbook>;
  clients: AdminClient[];
  users: Record<string, { _id: string; email: string; profile?: { name?: string; position?: string } }>;
  leads: Record<string, Lead[]>;
  stores: Record<string, Array<{ _id: string; storeCod: string; storeName: string; id_passbook: string }>>;
  products: Record<string, Product[]>;
  banners: Record<string, Banner[]>;
  fabButtons: Record<string, FabButton[]>;
  campaigns: Record<string, { email: Campaign[]; whatsapp: Campaign[]; automation: Campaign[]; upload: import('@/shared/types/campaign').MetabaseCampaign[] }>;
  stats: Record<string, { serialNumbers: number; android: number; ios: number }>;
}

const CNPJ_BY_STORE: Record<string, string> = { [TEASHOP]: '49.352.829/0001-42' };

function buildClients(passbooks: Record<string, Passbook>): { clients: AdminClient[]; users: MockDb['users'] } {
  const users: MockDb['users'] = {};
  const clients: AdminClient[] = Object.values(passbooks).map((pb, i) => {
    const userId = `user_${pb._id}`;
    users[userId] = {
      _id: userId,
      email: `gestor.${pb.organizationName.toLowerCase().replace(/[^a-z]/g, '')}@fixe.me`,
      profile: { name: `Gestor ${pb.organizationName}`, position: 'Gerente' },
    };
    return {
      _id: `client_${pb._id}`,
      name: pb.organizationName,
      cnpj: CNPJ_BY_STORE[pb._id] ?? `${pad(10 + i, 2)}.${pad(100 + i, 3)}.${pad(200 + i, 3)}/0001-${pad(10 + i, 2)}`,
      users: [userId],
      inactive: false,
      email: users[userId].email,
      phone: `61 9${pad(8000 + i, 4)}-${pad(1000 + i, 4)}`,
      passbooks: [{ _id: pb._id, shortUrl: pb.shortUrl, backgroundColor: pb.backgroundColor }],
      models: pb.hasGiftcard ? ['giftcard'] : pb.hasCashback ? ['cashback'] : ['stamps'],
    };
  });
  return { clients, users };
}

/* ──────────────────────────── montagem do db ──────────────────────────── */

export function createDb(): MockDb {
  const passbooks = buildPassbooks();
  const { clients, users } = buildClients(passbooks);

  const leads: MockDb['leads'] = {
    [TEASHOP]: buildLeads(TEASHOP, 64, false),
    pb_demo_loccitane: buildLeads('pb_demo_loccitane', 12, false),
    [ECARE]: buildLeads(ECARE, 48, true),
    [BACIO]: buildLeads(BACIO, 37, false),
    [OP_SELOS]: boostStampBalances(buildLeads(OP_SELOS, 24, false)),
    [OP_CASHBACK]: buildLeads(OP_CASHBACK, 24, true),
    [OP_PONTOS]: buildLeads(OP_PONTOS, 24, false),
    [OP_GIFTCARD]: buildLeads(OP_GIFTCARD, 16, true),
    [OP_REGUA]: buildLeads(OP_REGUA, 16, true),
    [OP_PRATILEIRA]: buildLeads(OP_PRATILEIRA, 16, false),
    [OP_TAXA]: boostStampBalances(buildLeads(OP_TAXA, 16, false)),
    [OP_PRODUTO]: boostStampBalances(buildLeads(OP_PRODUTO, 20, false)),
    [OP_EUR]: buildLeads(OP_EUR, 16, true),
    [OP_ADDFIELDS]: buildLeads(OP_ADDFIELDS, 16, true),
  };

  const stores: MockDb['stores'] = {
    [TEASHOP]: MOCK_STORES.map((s, i) => ({
      _id: `${TEASHOP}-store-${i}`,
      storeCod: s.storeCod,
      storeName: s.storeName,
      id_passbook: TEASHOP,
    })),
  };

  const products: MockDb['products'] = {
    [TEASHOP]: buildTeaShopProducts(TEASHOP),
    [ECARE]: buildTeaShopProducts(ECARE),
    [BACIO]: buildTeaShopProducts(BACIO),
    [OP_SELOS]: buildStampsDiscountCatalog(OP_SELOS),
    [OP_TAXA]: buildStampsDiscountCatalog(OP_TAXA),
    [OP_CASHBACK]: buildRedemptionCatalog(OP_CASHBACK),
    [OP_PONTOS]: buildRedemptionCatalog(OP_PONTOS),
    [OP_PRATILEIRA]: buildRedemptionCatalog(OP_PRATILEIRA),
    [OP_GIFTCARD]: buildRedemptionCatalog(OP_GIFTCARD),
    [OP_REGUA]: buildRedemptionCatalog(OP_REGUA),
    [OP_PRODUTO]: buildProductRuleCatalog(OP_PRODUTO),
    [OP_EUR]: buildRedemptionCatalog(OP_EUR),
    [OP_ADDFIELDS]: buildRedemptionCatalog(OP_ADDFIELDS),
  };

  const banners: MockDb['banners'] = {
    [TEASHOP]: buildBanners(TEASHOP),
    [ECARE]: buildBanners(ECARE),
    [BACIO]: buildBanners(BACIO),
  };

  const fabButtons: MockDb['fabButtons'] = {
    [TEASHOP]: buildFabButtons(TEASHOP),
    [ECARE]: buildFabButtons(ECARE),
    [BACIO]: buildFabButtons(BACIO),
  };

  const campaigns: MockDb['campaigns'] = {};
  const stats: MockDb['stats'] = {};
  let automationSeed = 0;
  for (const id of Object.keys(passbooks)) {
    campaigns[id] = {
      email: buildCampaigns(id, 'email'),
      whatsapp: buildCampaigns(id, 'whatsapp'),
      automation: buildAutomationCampaigns(id, automationSeed++),
      upload: [
        {
          _id: `${id}-camp-upload-01`,
          name: 'Campanha BI Junho',
          description: 'Disparo segmentado via Metabase',
          updated: daysAgoIso(2),
          received: 0,
          users: Array.from({ length: 120 }, (_, k) => `SN${k}`),
          data_disparo: '20/06/2026',
          horario: '10:00',
          performance: 7,
        },
      ],
    };
    const all = leads[id] ?? [];
    stats[id] = {
      serialNumbers: all.length,
      ios: all.filter(
        (l) =>
          (l.deviceLibraryIdentifier?.length ?? 0) > 0 ||
          (l.deviceToken ?? []).some(
            (t) => typeof t === 'object' && t !== null && (t as { typeAccess?: string }).typeAccess === 'APP-ios',
          ),
      ).length,
      android: all.filter(
        (l) =>
          (l.deviceGWallet?.length ?? 0) > 0 ||
          (l.deviceToken ?? []).some(
            (t) => typeof t === 'object' && t !== null && (t as { typeAccess?: string }).typeAccess === 'APP-android',
          ),
      ).length,
    };
  }

  return { passbooks, clients, users, leads, stores, products, banners, fabButtons, campaigns, stats };
}

/** Garante saldos variados p/ cartela + catálogo de selos. */
function boostStampBalances(leads: Lead[]): Lead[] {
  return leads.map((l, i) => {
    if (i % 5 === 0) return { ...l, total_points: Math.max(l.total_points ?? 0, 25) }; // cartela + Vale 50
    if (i % 3 === 0) return { ...l, total_points: Math.max(l.total_points ?? 0, 12) }; // cartela + Vale 20
    if (i % 3 === 1) return { ...l, total_points: Math.min(l.total_points ?? 3, 4) }; // sem cartela
    return l;
  });
}

/* ──────────────────────────── usuários de login ──────────────────────────── */

export const STORE_IDS = {
  TEASHOP,
  ECARE,
  BACIO,
  OP_SELOS,
  OP_CASHBACK,
  OP_PONTOS,
  OP_GIFTCARD,
  OP_REGUA,
  OP_PRATILEIRA,
  OP_TAXA,
  OP_PRODUTO,
  OP_EUR,
  OP_ADDFIELDS,
};

/** Mapeia o e-mail informado no login para uma role (apenas no mock). */
export function userFromEmail(email: string): PanelUser {
  const e = email.toLowerCase();
  if (e.includes('owner') || e.includes('dono')) {
    return { _id: 'u_owner', name: 'Owner Demo', email, role: 'Owner', hasCampaignAdvanced: true, metabase: { dashboard: 900 } };
  }
  if (e.includes('nps')) {
    return { _id: 'u_nps', name: 'NPS Demo', email, role: 'Nps', metabase: { dashboard: 910 } };
  }
  if (e.includes('operador') || e.includes('operator')) {
    return {
      _id: 'u_operator',
      name: 'Operador Demo',
      email,
      role: 'Operator',
      id_passbooks: OP_CASHBACK,
      position: 'Demo Cashback (Operador)',
    };
  }
  if (e.includes('produto') || e.includes('sku')) {
    return {
      _id: 'u_op_produto',
      name: 'Operador Produto',
      email,
      role: 'Operator',
      id_passbooks: OP_PRODUTO,
      position: 'Demo Selos por Produto (Operador)',
    };
  }
  if (e.includes('eur') || e.includes('euro') || e.includes('pt@')) {
    return {
      _id: 'u_op_eur',
      name: 'Operador EUR',
      email,
      role: 'Operator',
      id_passbooks: OP_EUR,
      position: 'Demo EUR (Operador)',
    };
  }
  if (e.includes('campos') || e.includes('addfields') || e.includes('cupom')) {
    return {
      _id: 'u_op_addfields',
      name: 'Operador Campos',
      email,
      role: 'Operator',
      id_passbooks: OP_ADDFIELDS,
      position: 'Demo Campos Extras (Operador)',
    };
  }
  if (e.includes('pontos') || e.includes('naopontua') || e.includes('pratileira')) {
    return {
      _id: 'u_op_pontos',
      name: 'Operador Pontos',
      email,
      role: 'Operator',
      id_passbooks: e.includes('pratileira') ? OP_PRATILEIRA : OP_PONTOS,
      position: e.includes('pratileira') ? 'Demo Pratileira (Operador)' : 'Demo Pontos (Operador)',
    };
  }
  if (e.includes('giftcard') || e.includes('gift')) {
    return {
      _id: 'u_op_giftcard',
      name: 'Operador Giftcard',
      email,
      role: 'Operator',
      id_passbooks: OP_GIFTCARD,
      position: 'Demo Giftcard (Operador)',
    };
  }
  if (e.includes('taxa')) {
    return {
      _id: 'u_op_taxa',
      name: 'Operador Selos Taxa',
      email,
      role: 'Operator',
      id_passbooks: OP_TAXA,
      position: 'Demo Selos Taxa (Operador)',
    };
  }
  if (e.includes('regua')) {
    return {
      _id: 'u_op_regua',
      name: 'Operador Régua',
      email,
      role: 'Operator',
      id_passbooks: OP_REGUA,
      position: 'Demo Régua Cashback (Operador)',
    };
  }
  if (e.includes('selos')) {
    return {
      _id: 'u_op_selos',
      name: 'Operador Selos',
      email,
      role: 'Operator',
      id_passbooks: OP_SELOS,
      position: 'Demo Selos (Operador)',
    };
  }
  if (e.includes('teashop')) {
    // Exceção de banners do legado: Client nessa loja precisa ver /banners.
    return { _id: 'u_client_teashop', name: 'Gestor TeaShop', email, role: 'Client', id_passbooks: TEASHOP };
  }
  if (e.includes('client') || e.includes('lojista') || e.includes('gestor')) {
    return { _id: 'u_client', name: 'Gestor Demo', email, role: 'Client', id_passbooks: OP_CASHBACK };
  }
  return { _id: 'u_admin', name: 'Admin Demo', email, role: 'Admin' };
}
